import { Tooltip } from '@sgme/ui';
import { useFormAnalytics } from 'components/form-analytic-provider';
import { FormattedMessage } from 'components/shared/Intl';
import { Key, Modifier, Shortcut } from 'components/shared/ShortcutManager';
import { ConnectedScheduleMenu } from 'components/Workspaces/Batch/Toolbar/BatchToolbarFormActions/ConnectedBatchScheduledMenu';
import { useSPIntl } from 'hooks/useIntl';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useLocale } from 'hooks/useLocale';
import { FC, useCallback } from 'react';

import { connectBatchToolbarFormActions } from './BatchToolbarFormActions.connect';
import type { BatchToolbarFormActionsProps, BatchToolbarFormActionsTrigger } from './BatchToolbarFormActions.models';

export const BatchToolbarFormActionsComponent: FC<BatchToolbarFormActionsProps> = ({
  canDeleteSelectedItems,
  canExportSelectedItems,
  canDuplicateSelectedItems,
  canPriceSelectedItems,
  canRefreshSelectedItemsDates,
  canAbortSelectedItems,
  canOpenInQuoteWorkspace,
  onDelete,
  onExportInternalExcel,
  onExportExternalExcel,
  onDuplicate,
  onPrice,
  onResetDates,
  onOpenInQuoteWorkspace,
  canOpenApplyBatchForm,
  toggleBatchMultiplyApply,
  onAbort,
  tabId,
}) => {
  const intl = useSPIntl();
  const [locale] = useLocale();
  const isInternal = useIsInternalUser();

  const exportInternalExcel = useCallback(
    () => onExportInternalExcel(intl, locale),
    [intl, locale, onExportInternalExcel],
  );
  const exportExternalExcel = useCallback(
    () => onExportExternalExcel(intl, locale),
    [intl, locale, onExportExternalExcel],
  );

  const { trackSubmitForm } = useFormAnalytics();

  const handlePrice = (trigger?: BatchToolbarFormActionsTrigger) => () => {
    onPrice(trigger);
    trackSubmitForm();
  };

  const priceButton = (
    <button
      className="btn btn-success px-2 px-md-4"
      disabled={!canPriceSelectedItems}
      onClick={handlePrice('button')}
      type="button"
      data-e2e="batch-price-form"
    >
      <FormattedMessage id="price" />
    </button>
  );

  return (
    <>
      {canDuplicateSelectedItems && (
        <Shortcut code={Key.m} modifier={Modifier.ctrl} onKeyPress={() => onDuplicate('shortcut')} />
      )}
      {canDeleteSelectedItems && (
        <Shortcut code={Key.delete} modifier={Modifier.ctrl} onKeyPress={() => onDelete('shortcut')} />
      )}
      {canPriceSelectedItems && (
        <Shortcut code={Key.enter} modifier={Modifier.ctrl} onKeyPress={handlePrice('shortcut')} />
      )}
      <div className="d-flex align-items-center">
        <Tooltip position="bottom" contents={<FormattedMessage id="openInQuoteWorkspace" />}>
          <div>
            <button
              className="btn btn-flat-primary btn-icon-end"
              disabled={!canOpenInQuoteWorkspace}
              onClick={() => onOpenInQuoteWorkspace()}
              type="button"
              data-e2e="batch-open-in-quote-workspace"
              id="batch-open-in-quote-workspace"
            >
              <span className="d-none d-lg-block">
                <FormattedMessage id="openInQuoteWorkspace" />
              </span>
              <i className="icon">open_in_new</i>
            </button>
          </div>
        </Tooltip>

        <Tooltip position="bottom" contents={<FormattedMessage id="batchPricer.apply" />}>
          <div>
            <button
              className="btn btn-flat-primary btn-icon-end"
              disabled={!canOpenApplyBatchForm}
              type="button"
              data-e2e="batch-apply-open-modal"
              id="batch-apply-open-modal"
              onClick={toggleBatchMultiplyApply}
            >
              <span className="d-none d-lg-block">
                <FormattedMessage id="batchPricer.apply" />
              </span>
              <i className="icon">playlist_add_check</i>
            </button>
          </div>
        </Tooltip>

        <Tooltip position="bottom" contents={<FormattedMessage id="export" />}>
          <div>
            <button
              className="btn btn-flat-primary btn-icon-end"
              disabled={!canExportSelectedItems}
              onClick={isInternal ? exportInternalExcel : exportExternalExcel}
              type="button"
              data-e2e={isInternal ? 'batch-export-internal-sales' : 'batch-export-external-client'}
              id={isInternal ? 'batch-export-internal-sales' : 'batch-export-external-client'}
            >
              <span className="d-none d-lg-block">
                <FormattedMessage id="export" />
              </span>
              <i className="icon">ios_share</i>
            </button>
          </div>
        </Tooltip>

        <Tooltip position="bottom" contents={<FormattedMessage id="schedule" />}>
          <div>
            <ConnectedScheduleMenu id={tabId} analyticContext="batch" isButtonLabelVisible={false} />
          </div>
        </Tooltip>

        <div className="vr mx-1 my-2 border-end"></div>

        <Tooltip position="bottom" contents={<FormattedMessage id="shortcut.CtrlPlusM" />}>
          <div>
            <button
              className="btn btn-flat-primary"
              disabled={!canDuplicateSelectedItems}
              onClick={() => onDuplicate('button')}
              type="button"
              data-e2e="batch-duplicate-form"
              id="batch-duplicate-form"
            >
              <i className="icon">content_copy</i>
            </button>
          </div>
        </Tooltip>

        <Tooltip position="bottom" contents={<FormattedMessage id="shortcut.CtrlPlusDelete" />}>
          <div>
            <button
              className="btn btn-flat-primary"
              disabled={!canDeleteSelectedItems}
              onClick={() => onDelete('button')}
              type="button"
              data-e2e="batch-delete-form"
              id="batch-delete-form"
            >
              <i className="icon">delete_outline</i>
            </button>
          </div>
        </Tooltip>

        <Tooltip position="bottom" contents={<FormattedMessage id="form.ResetMainDates" />}>
          <div>
            <button
              className="btn btn-flat-primary"
              disabled={!canRefreshSelectedItemsDates}
              onClick={onResetDates}
              type="button"
              data-e2e="batch-refresh-form-dates"
              id="batch-refresh-form-dates"
            >
              <i className="icon">history</i>
            </button>
          </div>
        </Tooltip>

        {canAbortSelectedItems && (
          <button
            className="btn btn-flat-primary me-2"
            onClick={onAbort}
            type="button"
            data-e2e="batch-abort"
            id="batch-abort"
          >
            <i className="d-lg-none icon">cancel</i>
            <span className="d-none d-lg-block">
              <FormattedMessage id="abort" />
            </span>
          </button>
        )}

        {canPriceSelectedItems ? (
          <Tooltip position="bottom" contents={<FormattedMessage id="shortcut.CtrlPlusEnter" />}>
            <div>{priceButton}</div>
          </Tooltip>
        ) : (
          priceButton
        )}
      </div>
    </>
  );
};

export const BatchToolbarFormActions = connectBatchToolbarFormActions(BatchToolbarFormActionsComponent);
